import { Icon as ConstellationIcon } from '@zillow/constellation-10';
import type { ComponentProps, ComponentType, SVGProps } from 'react';
import type { LucideIcon as LucideIconType } from 'lucide-react';
import { token } from '@/styled-system/tokens';

// Accept either a real lucide-react icon (a ForwardRefExoticComponent — what
// `import { Home } from 'lucide-react'` resolves to) or a plain SVG component
// for the handful of bespoke icons under src/components/ui/icons/.
export type LucideIconComponent = LucideIconType | ComponentType<SVGProps<SVGSVGElement>>;

export type LucideIconProps = Omit<ComponentProps<typeof ConstellationIcon>, 'children' | 'render'> & {
  icon: LucideIconComponent;
  /**
   * Stroke color for Lucide-style stroke icons. Accepts any CSS color string
   * (raw value, CSS var, `token.var('colors.*')`). Defaults to `currentColor`.
   */
  color?: string;
  /**
   * Fill for the SVG path. Defaults to `none` because Lucide icons are
   * stroke-first; pass `currentColor` for saved/selected filled states.
   */
  fill?: string;
  /**
   * When true, renders the raw SVG element directly — no Constellation `<Icon>`
   * wrapper. Use this when passing LucideIcon as the `render` prop of a
   * Constellation component (e.g. `<Menu.ItemIcon render={<LucideIcon asRender />}`),
   * where both components are `<Icon>` under the hood and nesting them would
   * produce conflicting size styles. The parent's Slot lands directly on the SVG.
   */
  asRender?: boolean;
};

const HP_ICON_CLASS = 'hp-icon';

// lucide repeats these on every <svg>; Constellation's `.c11n { all: unset }` already overrides them,
// and `svg.hp-icon` supplies the values. Undefined drops the attribute from the markup.
const NO_PRESENTATION_ATTRIBUTES = {
  xmlns: undefined,
  fill: undefined,
  stroke: undefined,
  strokeWidth: undefined,
  strokeLinecap: undefined,
  strokeLinejoin: undefined,
} as const;

/**
 * Renders a Lucide icon via Constellation's `<Icon>` slot wrapper. Forwards
 * Constellation Icon props (`size`, `css`, etc.) while handling `title`
 * locally: pass `title` for an accessible name (`aria-label` plus a plain SVG
 * `<title>`); omit it for a decorative icon (`aria-hidden`).
 *
 * Stroke defaults (`fill: none`, `stroke: currentColor`, width 2, round caps and
 * joins) come from one `svg.hp-icon` rule in `styles/globals.css`. It sits outside
 * Panda's cascade layers, so it beats Constellation's `fill: currentColor` recipe
 * and the preflight's scoped `.c11n { all: unset }`, which wipes SVG presentation
 * attributes. Only a non-default `color` or `fill` is applied inline.
 *
 * For Constellation's own filled brand glyphs use `<Icon>` from
 * `@zillow/constellation-10` directly — this wrapper is Lucide-shaped.
 *
 * Pass `asRender` when using as the `render` prop of another Constellation
 * `<Icon>`-based component to avoid double-Icon nesting.
 */
export function LucideIcon({
  icon: IconComponent,
  color = 'currentColor',
  fill = 'none',
  style,
  title,
  asRender = false,
  ...rest
}: LucideIconProps) {
  const titleProps = title
    ? {
        'aria-label': rest['aria-label'] ?? title,
        'aria-hidden': 'false' as const,
      }
    : undefined;
  // Default stroke styling comes from `svg.hp-icon` in globals.css; only non-default paint stays inline.
  const paintStyle = color !== 'currentColor' || fill !== 'none' ? { fill, stroke: color } : undefined;
  const icon = title ? (
    <IconComponent className={HP_ICON_CLASS} {...NO_PRESENTATION_ATTRIBUTES}>
      <title>{title}</title>
    </IconComponent>
  ) : (
    <IconComponent className={HP_ICON_CLASS} {...NO_PRESENTATION_ATTRIBUTES} />
  );

  if (asRender) {
    // Drop Constellation-only props (size, css) that are invalid on raw SVG elements.
    // Everything else (className, role, aria-*, data-*, focusable) is forwarded so the
    // parent's Slot can land its grid-area/sizing styles on the SVG.
    // Size is resolved as an inline style so it beats any class-based size set by the parent recipe.
    const { size, css: _css, className, ...svgProps } = rest as typeof rest & { size?: string; css?: unknown };
    const sizeStyle = size
      ? {
          width: token.var(`sizes.icon.${size}` as Parameters<typeof token.var>[0]),
          height: token.var(`sizes.icon.${size}` as Parameters<typeof token.var>[0]),
        }
      : undefined;
    const svgStyle = paintStyle || sizeStyle || style ? { ...paintStyle, ...sizeStyle, ...style } : undefined;
    return (
      <IconComponent
        {...NO_PRESENTATION_ATTRIBUTES}
        style={svgStyle}
        className={className ? `${HP_ICON_CLASS} ${className}` : HP_ICON_CLASS}
        {...svgProps}
        {...titleProps}
      >
        {title && <title>{title}</title>}
      </IconComponent>
    );
  }

  return (
    <ConstellationIcon style={paintStyle || style ? { ...paintStyle, ...style } : undefined} {...rest} {...titleProps}>
      {icon}
    </ConstellationIcon>
  );
}
